import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { User } from '../../models';
import { Team } from '../../models/team';
import { UserService } from '../../services/user-service/user.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-team-form',
  templateUrl: './team-form.component.html',
  styleUrls: ['./team-form.component.scss']
})
export class TeamFormComponent implements OnInit {
  @Input() waitOnAction = false;
  @Input() editTarget: Team = null;

  @Output() createTeam = new EventEmitter<Team>();
  @Output() editTeam = new EventEmitter<Team>();

  availableUsers: User[] = [];
  selectedMembers: User[] = [];
  selectedLead: User = null;
  availableFilter = '';
  waitOnInit = true;

  get filteredAvailableUsers(): User[] {
    const q = this.availableFilter.trim().toLowerCase();
    return q
      ? this.availableUsers.filter(u => this.userLabel(u).toLowerCase().includes(q))
      : this.availableUsers;
  }

  constructor(
    private userService: UserService,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.userService.getAll().subscribe({
      next: (users: User[]) => {
        var memberIds = new Set((this.editTarget?.members || []).map(m => m.id));
        if (this.editTarget) {
          memberIds.add(this.editTarget.lead?.id);
        }
        this.availableUsers = users.filter(u => !memberIds.has(u.id));
        this.selectedMembers = [...(this.editTarget?.members || [])];
        if (this.editTarget?.lead) {
          this.selectedMembers.unshift(this.editTarget.lead);
        }
        this.selectedLead = this.editTarget?.lead || undefined;
        this.waitOnInit = false;
      },
      error: err => {
        this.toastrService.error(err);
        this.waitOnInit = false;
      }
    });
  }

  onDrop(event: CdkDragDrop<User[]>) {
    const fromAvailable = event.previousContainer.data === this.availableUsers;

    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    if (fromAvailable) {
      // Map rendered (filtered) index back to the real availableUsers index
      const droppedUser = this.filteredAvailableUsers[event.previousIndex];
      const realIndex = this.availableUsers.indexOf(droppedUser);
      this.availableUsers.splice(realIndex, 1);
      this.selectedMembers.splice(event.currentIndex, 0, droppedUser);
    } else {
      // Moving from selectedMembers back to available
      const droppedUser = this.selectedMembers[event.previousIndex];
      this.selectedMembers.splice(event.previousIndex, 1);
      this.availableUsers.push(droppedUser);
      if (this.selectedLead?.id === droppedUser.id) {
        this.selectedLead = null;
      }
    }
  }

  onLeadChange(event: Event) {
    const id = parseInt((event.target as HTMLSelectElement).value, 10);
    this.selectedLead = this.selectedMembers.find(u => u.id === id) || null;
  }

  userLabel(user: User): string {
    return `${user?.firstName} ${user?.lastName}`;
  }

  onSubmit() {
    if (!this.selectedLead) {
      this.toastrService.error('Please select a team lead.');
      return;
    }
    const teamData: Team = new Team({ id: this.editTarget?.id, lead: this.selectedLead, members: this.selectedMembers.filter(m => m.id != this.selectedLead.id) });
    if (this.editTarget) {
      this.editTeam.emit(teamData);
    } else {
      this.createTeam.emit(teamData);
    }
  }
}
